import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import Button from '@/components/ui/Button';

import {
  Loader2,
  Download,
  ChevronRight,
  FileText,
  ArrowLeft,
  CheckCircle2,
  Calendar
} from 'lucide-react';

const DocumentDownloadPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get source tracking token from URL
  const sourceToken = searchParams.get('src');

  const [bundle, setBundle] = useState<{
    title: string;
    isAllDocuments: boolean;
    company?: { name: string; logoUrl?: string } | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const [fetchError, setFetchError] = useState<string | null>(null);
  const [leadError, setLeadError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [resultDocs, setResultDocs] = useState<{ id: string; title: string; originalFilename: string; downloadUrl: string }[]>([]);
  const [zipping, setZipping] = useState(false);
  const [page, setPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);

  const isValidEmail = (value: string) => /\S+@\S+\.\S+/.test(value);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      setFetchError('Missing context id in URL');
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        // Try fetching as Bundle first
        try {
          const res = await api.get(`/mxm/leads/pages/${id}`);
          if (res.data) {
            setBundle(res.data);
            setLoading(false);
            return;
          }
        } catch (e) {
          // Not a bundle, proceed to try single document metadata
        }

        // Single doc check
        await api.get(`/upload/${id}/url`);
        // If it exists, we just wait for email submission which handles the actual URL generation
      } catch (err: unknown) {
        setFetchError('Content not found.');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  const handleDownloadAll = async () => {
    // Server-side streaming zip download
    if (totalDocs === 0) return;
    setZipping(true);
    setLeadError(null);

    try {
      const envApi = import.meta.env.VITE_APP_API_URL || '';
      const baseUrl = envApi.replace(/\/$/, '');
      const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;

      const downloadUrl = `${apiBase}/mxm/leads/pages/${id}/download-zip`;
      window.open(downloadUrl, '_blank');

    } catch (err: unknown) {
      console.error('Download Error:', err);
      setLeadError("Failed to download zip file.");
    } finally {
      setZipping(false);
    }
  };

  const handleDownloadSingle = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEmailSubmit = async () => {
    if (!email || !isValidEmail(email)) {
      setEmailError('Valid email required');
      return;
    }

    try {
      setSubmitting(true);
      setLeadError(null);

      const response = await api.post('/mxm/leads', {
        email,
        documentId: id,
        sourceUrl: window.location.href,
        ...(sourceToken && { src: sourceToken }),
      });

      if (response.data && response.data.bookingUrl) {
        setBookingUrl(response.data.bookingUrl);
      }

      if (response.data && response.data.documents && response.data.documents.length > 0) {
        setResultDocs(response.data.documents);
        setTotalDocs(response.data.total || response.data.documents.length);
        setPage(1);
      } else if (response.data && response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        setLeadError("Download generation failed.");
      }
    } catch (err: unknown) {
      setLeadError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage === page || submitting || loadingDocs) return;

    setLoadingDocs(true);
    try {
      const res = await api.post(`/mxm/leads/pages/${id}/documents`, {
        email,
        page: newPage,
        limit: 10
      });

      if (res.data && res.data.documents) {
        setResultDocs(res.data.documents);
        setPage(newPage);
        // Optionally update total if changed
        if (res.data.total) setTotalDocs(res.data.total);
      }
    } catch (error) {
      console.error("Pagination failed", error);
    } finally {
      setLoadingDocs(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFC] flex flex-col items-center justify-center p-6">
        <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
      </div>
    );
  }

  const totalPages = Math.ceil(totalDocs / 10);

  return (
    <div className="min-h-screen bg-[#FBFBFC] font-sans selection:bg-blue-100 selection:text-blue-900">
      {/* Top Professional Banner */}
      <nav className="w-full border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {bundle?.company?.logoUrl ? (
              <img src={bundle.company.logoUrl} alt={bundle.company.name} className="h-6 w-auto" />
            ) : (
              <>
                <div className="w-7 h-7 bg-slate-900 rounded flex items-center justify-center text-[11px] font-bold text-white">
                  {bundle?.company?.name?.charAt(0) || 'C'}
                </div>
                <span className="text-xs font-bold text-slate-900 uppercase tracking-widest">
                  {bundle?.company?.name || 'Carion Portal'}
                </span>
              </>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-20">
        {fetchError ? (
          <div className="bg-white border border-red-100 rounded-xl p-10 text-center shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Access Limited</h2>
            <p className="text-sm text-slate-500 mb-6">{fetchError}</p>
            <Button onClick={() => navigate('/')} className="h-10 px-6">Return to Portal</Button>
          </div>
        ) : resultDocs.length > 0 ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="mb-10 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 rounded-full mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Access Granted</h1>
              <p className="text-sm text-slate-500">The following documents are now available for download.</p>
            </div>

            {bookingUrl && (
              <div className="mb-10 p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl text-white shadow-xl shadow-slate-200 relative overflow-hidden group">
                {/* Decorative background element */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-colors" />

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center shrink-0 border border-white/10">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold tracking-tight">Ready for a deeper dive?</h3>
                      <p className="text-xs text-slate-400 mt-0.5 font-medium">Schedule a callback with our strategy team.</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => window.open(bookingUrl, '_blank')}
                    className="h-11 px-8 bg-blue-600 hover:bg-blue-500 text-white font-bold uppercase tracking-widest text-[10px] border-none shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-all whitespace-nowrap"
                  >
                    Schedule Meeting
                    <ChevronRight className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-4 px-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Documents ({totalDocs})</span>
              {totalDocs > 0 && resultDocs.length > 1 && (
                <button
                  onClick={handleDownloadAll}
                  disabled={zipping}
                  className="flex items-center gap-2 text-[10px] font-bold text-blue-600 hover:text-blue-700 uppercase tracking-widest transition-colors disabled:opacity-50"
                >
                  {zipping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  Download Page (ZIP)
                </button>
              )}
            </div>

            <div className={`space-y-2 mb-8 ${loadingDocs ? 'opacity-50 pointer-events-none' : ''}`}>
              {resultDocs.map((d) => (
                <div
                  key={d.id}
                  className="group bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between hover:border-blue-400 hover:shadow-md transition-all cursor-default"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-slate-50 rounded flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors">
                      <FileText className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate tracking-tight">{d.title}</p>
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">
                        {d.originalFilename?.split('.').pop() || 'PDF'} ASSET
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadSingle(d.downloadUrl)}
                    className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-900 hover:border-slate-900 hover:text-white transition-all shadow-sm shrink-0"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mb-8">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1 || loadingDocs}
                  className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1"
                >
                  Previous
                </button>
                <span className="text-xs font-medium text-slate-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || loadingDocs}
                  className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1"
                >
                  Next
                </button>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={() => setResultDocs([])}
                className="flex items-center gap-2 mx-auto text-[10px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-widest transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Finish Session
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-in zoom-in-95 duration-500">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">Document Access</h1>
                <p className="text-xs text-slate-400 font-medium">Please verify your email to continue</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                <p className="text-xs font-medium text-slate-600 leading-relaxed italic">
                  "{bundle ? bundle.title : 'Shared Document'}"
                </p>
                <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase tracking-tight">Document Collection</p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Work Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder="john@example.com"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition-all text-sm font-medium placeholder:text-slate-300 shadow-sm"
                />
                {emailError && <p className="text-[10px] font-bold text-red-500 ml-1">{emailError}</p>}
              </div>

              {leadError && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-xs text-red-600 font-bold text-center">{leadError}</p>
                </div>
              )}

              <button
                onClick={handleEmailSubmit}
                disabled={submitting || !email}
                className={`w-full h-12 rounded-xl flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-widest transition-all shadow-sm
                  ${submitting || !email
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 text-white hover:bg-slate-800 active:scale-[0.98]'
                  }`}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Access Assets
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="text-[10px] text-slate-400 text-center font-medium leading-relaxed">
                By accessing these documents, you agree to our security protocols.<br />Assets are encrypted and tracked for enterprise compliance.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DocumentDownloadPage;

